import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Label,
  Badge,
  InputGroup,
  InputGroupText,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import toast from "react-hot-toast";
import OrderServices from "../../../services/OrderingServices/OrderServices";
import StatusUpdateModal from "../component/StatusUpdateModal";
import CancelModal from "../component/CancelModal";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import TableSearch from "../../../Components/Common/TableSearch";

// ✅ STEP 1: Import the reusable hook
import useServerSideSearch from "../../../hooks/useServerSideSearch";

const OrderDetailsList = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // Filter states
  const [filters, setFilters] = useState({
    refNumber: "",
    productName: "",
    startDate: today,
    endDate: today,
  });

  // ✅ STEP 2: Use the hook with custom filters
  const {
    data: orderDetailsList,
    loading,
    totalRows,
    stats: apiStats,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handleFilterChange,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(
    OrderServices.getOrderDetailList,
    [],
    500,
    (response) => {
      // Custom response mapper
      const mappedItems = (response.items || []).map((item) => ({
        id: item?.orderId || 0,
        productId: item?.productId || 0,
        productName: item?.productName || "N/A",
        unitName: item?.unitName || "N/A",
        productPrice: item?.productPrice || 0,
        quantity: item?.quantity || 0,
        complimentaryQuantity: item?.complimentaryQuantity || 0,
        finalQuantity: item?.finalQuantity || 0,
        totalPrice: item?.totalPrice || 0,
        refNumber: item?.refNumber || "N/A",
        orderStatus: item?.orderStatus || "Ordered",
      }));

      return {
        data: mappedItems,
        totalCount: response.totalCount || 0,
        stats: response.summary || {},
      };
    }
  );

  // Modal states
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Apply filters to the hook
  useEffect(() => {
    const fromDate = filters.startDate ? `${filters.startDate}T00:00:00` : "";
    const toDate = filters.endDate ? `${filters.endDate}T23:59:59` : "";

    handleFilterChange({
      refNumber: filters.refNumber,
      productName: filters.productName,
      fromDate,
      toDate,
    });
  }, [filters]);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFilterInputChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    const today = new Date();
    setFilters({
      refNumber: "",
      productName: "",
      startDate: formatLocalDate(today),
      endDate: formatLocalDate(today),
    });
  };

  const handleCancel = async () => {
    try {
      if (!selectedItem) return;
      await OrderServices.cancelOrder(selectedItem.id);
      refetch();
      setIsCancelModalOpen(false);
      toast.success("Order cancelled successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to cancel order");
    }
  };

  // ✅ STEP 3: Define your columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Reference No",
      selector: (row) => row.refNumber,
      cell: (row) => (
        <Link to={`/order/${row.refNumber}`}>{row.refNumber}</Link>
      ),
      sortable: true,
      wrap: true,
      width: "20vw",
    },
    {
      name: "Product Name",
      selector: (row) => row.productName,
      sortable: true,
      wrap: true,
      width: "15vw",
    },
    {
      name: "Unit",
      selector: (row) => row.unitName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.finalQuantity,
      sortable: true,
      center: true,
    },
    {
      name: "Price",
      selector: (row) => row.productPrice || 0,
      cell: (row) => <NumberWithCommas number={row.productPrice} />,
      sortable: true,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => row.totalPrice || 0,
      cell: (row) => <NumberWithCommas number={row.totalPrice} />,
      sortable: true,
      right: true,
    },
  ];

  // ✅ STEP 4: Define your stats
  const stats = [
    {
      title: "Total Items",
      value: apiStats?.totalItems || 0,
      color: "#0d6efd",
      icon: "bx bx-receipt",
    },
    {
      title: "Sales Count",
      value: apiStats?.totalSalesCount || 0,
      color: "#28a745",
      icon: "bx bx-dollar-circle",
    },
    {
      title: "Total Amount",
      value: <NumberWithCommas number={apiStats?.totalAmount || 0} />,
      color: "#17a2b8",
      icon: "bx bx-category",
    },
    {
      title: "Discount Amount",
      value: <NumberWithCommas number={apiStats?.totalDiscountAmount || 0} />,
      color: "#ffc107",
      icon: "bx bx-dollar",
    },
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.refNumber) count++;
    if (filters.productName) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // ✅ STEP 5: Render
  return (
    <>
      <ResponsiveDataManagement
        title="Order Details"
        pageTitle="Order Management"
        data={orderDetailsList}
        columns={columns}
        stats={stats}
        searchKeys={[
          "refNumber",
          "productName",
          "unitName",
          "finalQuantity",
          "productPrice",
          "totalPrice",
        ]}
        loading={loading}
        emptyStateMessage="No order details found"
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        onPaginationChange={handlePaginationChange}
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePaginationChange}
        onChangeRowsPerPage={(newPerPage, page) =>
          handlePaginationChange({ page, perPage: newPerPage })
        }
        showBreadcrumb={true}
        showSearch={false}
        customHeaderContent={
          <>
            {/* Desktop-only filters */}
            <Row className="d-none d-lg-flex">
              <Col md={5}>
                <h5 className="mb-1 fw-bold text-dark">Order Details List</h5>
                {filters.startDate === today ? (
                  <p className="text-muted small mb-0">Today's Order Details</p>
                ) : (
                  <p className="text-muted small mb-0">
                    Showing {filters.startDate} to {filters.endDate}
                  </p>
                )}
              </Col>
              <Col md={7}>
                <div className="d-flex justify-content-end align-items-center gap-2">
                  {/* Search Bar */}
                  <TableSearch
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search by all..."
                    style={{ width: "100%" }}
                  />

                  {/* Date Range Picker */}
                  <InputGroup style={{ maxWidth: "300px" }}>
                    <Flatpickr
                      value={
                        filters.startDate && filters.endDate
                          ? `${filters.startDate} to ${filters.endDate}`
                          : ""
                      }
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                      }}
                      onChange={(dates) => {
                        if (dates.length === 2) {
                          const start = formatLocalDate(dates[0]);
                          const end = formatLocalDate(dates[1]);
                          setFilters((prev) => ({
                            ...prev,
                            startDate: start,
                            endDate: end,
                          }));
                        }
                      }}
                      placeholder="Select Date Range"
                      className="form-control"
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>
                </div>
              </Col>
            </Row>
          </>
        }
        mobileCardRenderer={(item, index) => (
          <>
            {/* Only show title once, on first item */}
            {index === 0 && (
              <div className="mb-3 d-flex justify-content-between align-items-center">
                {/* Left side */}
                <div>
                  <h5 className="fw-bold text-dark fs-4">Order Details List</h5>
                  <p className="text-muted small mb-0">
                    {filters.startDate === today
                      ? "Today's Order Details"
                      : `${filters.startDate} to ${filters.endDate}`}
                  </p>
                </div>

                {/* Right side - Filter Button */}
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={() => setFilterModalOpen(true)}
                  className="position-relative"
                >
                  <i className="bx bx-filter-alt" />
                  {activeFilterCount > 0 && (
                    <Badge
                      color="danger"
                      pill
                      className="position-absolute top-0 end-0 translate-middle-x"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>
            )}

            <Card
              key={`${item.refNumber}-${item.productId}-${index}`}
              className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
              style={{
                cursor: "pointer",
                borderLeft: "4px solid #5B71B9",
                transition: "all 0.2s",
              }}
              onClick={() => navigate(`/order/${item.refNumber}`)}
            >
              <CardBody className="py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="fw-bold text-primary mb-1">
                      {item.refNumber}
                    </h6>

                    <p className="small text-muted mb-1">
                      {item.productName} • {item.unitName}
                    </p>
                    {/* <p className="small text-muted mb-1">
                      {formatDateTime(item.createdDate)} •{" "}
                      {item.tableName || "Takeaway"}
                    </p> */}
                    <p className="small text-muted mb-0">
                      {item.finalQuantity} • Rs.{" "}
                      <NumberWithCommas number={item.productPrice} />
                    </p>
                    <h5 className="fw-bold text-success mb-2">
                      Rs. <NumberWithCommas number={item.totalPrice} />
                    </h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      />

      {/* Mobile Filter Offcanvas */}
      <Offcanvas
        isOpen={filterModalOpen}
        toggle={() => setFilterModalOpen(!filterModalOpen)}
        direction="bottom"
        backdrop={true}
        scrollable
        style={{
          height: "85vh",
          maxHeight: "90vh",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
        }}
      >
        <OffcanvasHeader
          toggle={() => setFilterModalOpen(!filterModalOpen)}
          className="border-bottom bg-light"
          style={{ borderTopLeftRadius: "24px", borderTopRightRadius: "24px" }}
        >
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-filter-alt fs-5"></i>
            <span className="fw-bold">Filter Order Details</span>
            {activeFilterCount > 0 && (
              <Badge color="primary" pill>
                {activeFilterCount} Active
              </Badge>
            )}
          </div>
        </OffcanvasHeader>

        <OffcanvasBody className="d-flex flex-column p-3">
          <div className="flex-grow-1">
            {/* Search */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-search me-1"></i>Search
              </Label>
              <TableSearch
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by all..."
                style={{ width: "100%" }}
              />
            </div>

            {/* Date Range */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-calendar me-1"></i>Date Range
              </Label>
              <Flatpickr
                value={
                  filters.startDate && filters.endDate
                    ? `${filters.startDate} to ${filters.endDate}`
                    : ""
                }
                options={{
                  mode: "range",
                  dateFormat: "Y-m-d",
                }}
                onChange={(dates) => {
                  if (dates.length === 2) {
                    const start = formatLocalDate(dates[0]);
                    const end = formatLocalDate(dates[1]);
                    setFilters((prev) => ({
                      ...prev,
                      startDate: start,
                      endDate: end,
                    }));
                  }
                }}
                placeholder="Select Date Range"
                className="form-control"
              />
            </div>

            {/* Reference Number */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-hash me-1"></i>Reference Number
              </Label>
              <input
                type="text"
                name="refNumber"
                value={filters.refNumber}
                onChange={(e) =>
                  handleFilterInputChange("refNumber", e.target.value)
                }
                placeholder="Enter reference number"
                className="form-control"
              />
            </div>

            {/* Product Name */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-package me-1"></i>Product Name
              </Label>
              <input
                type="text"
                name="productName"
                value={filters.productName}
                onChange={(e) =>
                  handleFilterInputChange("productName", e.target.value)
                }
                placeholder="Enter product name"
                className="form-control"
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            className="bg-white border-top pt-3 d-flex gap-2"
            style={{ marginTop: "auto" }}
          >
            <Button
              color="light"
              className="w-50 border shadow-sm"
              onClick={handleClearFilters}
            >
              <i className="bx bx-x me-1"></i>Clear All
            </Button>

            <Button
              color="primary"
              className="w-50 shadow-sm"
              onClick={() => setFilterModalOpen(false)}
            >
              <i className="bx bx-check me-1"></i>Apply Filters
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>

      {/* Modals */}
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        toggle={() => setIsStatusModalOpen(!isStatusModalOpen)}
        orderDetailId={selectedItem?.refNumber}
        currentStatus={selectedItem?.orderStatus}
        fetchOrderList={refetch}
      />

      <CancelModal
        isOpen={isCancelModalOpen}
        toggle={() => setIsCancelModalOpen(!isCancelModalOpen)}
        onDeleteClick={handleCancel}
      />
    </>
  );
};

export default OrderDetailsList;
